import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { motion } from 'motion/react';
import {
  MapPin,
  Sun,
  Leaf,
  Minus,
  Plus,
  ShoppingBasket,
  ChevronLeft,
  CalendarDays,
  ShieldCheck,
  Video,
  Sprout,
} from 'lucide-react';
import { toast } from 'sonner';

import {
  formatRupeesCompact,
  discountPercent,
  lineTotal,
  formatRupees,
  UNIT_LABELS,
  SOURCE_LABELS,
  PRACTICE_LABELS,
} from '@sreerasa/shared';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useProduct, useProductMedia } from '@/lib/queries';
import { productImageUrl } from '@/lib/supabase';
import { useCart } from '@/features/cart/cart-store';
import { cn, relativeDay, daysUntil } from '@/lib/utils';
import { Reveal, transitions } from '@/components/motion/primitives';

export const Route = createFileRoute('/shop/$slug')({
  component: ProductPage,
});

function ProductPage() {
  const { slug } = Route.useParams();
  const { data: product, isLoading } = useProduct(slug);
  const { data: media } = useProductMedia(product?.id);

  const [quantity, setQuantity] = useState<number | null>(null);
  const [activeMedia, setActiveMedia] = useState(0);
  const add = useCart((state) => state.add);

  if (isLoading) return <ProductSkeleton />;

  if (!product) {
    return (
      <div className="mx-auto max-w-lg px-4 py-32 text-center">
        <Sprout className="mx-auto size-10 text-soil-300" aria-hidden />
        <h1 className="mt-4 font-display text-2xl font-bold text-soil-900">Not found</h1>
        <p className="mt-2 text-soil-600">This listing is gone — sold out, or the season ended.</p>
        <Link to="/shop" className="mt-6 inline-block">
          <Button>Browse the shop</Button>
        </Link>
      </div>
    );
  }

  // Quantity starts at the product's minimum, which isn't known until the
  // product loads — hence the null-until-hydrated dance.
  const qty = quantity ?? product.min_order_qty;
  const discount = discountPercent(product.price_paise, product.compare_at_paise);
  const isOut = product.status === 'out_of_stock' || product.stock_qty <= 0;
  const maxQty = Math.min(product.max_order_qty ?? Infinity, product.stock_qty);
  const shelfLife = daysUntil(product.best_before);

  const gallery = media?.length
    ? media
    : product.primary_image
      ? [
          {
            id: 'primary',
            storage_path: product.primary_image,
            media_type: 'image',
            caption: null,
            is_proof: false,
          },
        ]
      : [];

  const current = gallery[activeMedia];
  const currentUrl = productImageUrl(current?.storage_path);

  function handleAdd() {
    add({
      product_id: product!.id,
      quantity: qty,
      title: product!.title,
      unit: product!.unit,
      image: productImageUrl(product!.primary_image),
    });
    toast.success('Added to your basket', {
      description: `${qty} ${UNIT_LABELS[product!.unit]} of ${product!.title}`,
    });
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <Link
        to="/shop"
        className="mb-6 inline-flex items-center gap-1.5 text-sm font-medium text-soil-500 transition-colors hover:text-farm-700"
      >
        <ChevronLeft className="size-4" aria-hidden />
        Back to the shop
      </Link>

      <div className="grid gap-10 lg:grid-cols-2">
        {/* ── Gallery ── */}
        <div className="space-y-3">
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={transitions.base}
            className="relative aspect-square overflow-hidden rounded-card border border-soil-200 bg-soil-100"
          >
            {currentUrl ? (
              current?.media_type === 'video' ? (
                <video src={currentUrl} controls playsInline className="size-full object-cover" />
              ) : (
                <motion.img
                  key={currentUrl}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  src={currentUrl}
                  alt={product.title}
                  className="size-full object-cover"
                />
              )
            ) : (
              <div className="flex size-full items-center justify-center bg-gradient-to-br from-field-200 to-harvest-100">
                <Leaf className="size-16 text-farm-600/25" aria-hidden />
              </div>
            )}

            {discount && (
              <Badge variant="sun" size="lg" className="absolute left-4 top-4 shadow-lift">
                {discount}% off
              </Badge>
            )}
          </motion.div>

          {gallery.length > 1 && (
            <div className="scrollbar-none flex gap-2.5 overflow-x-auto pb-1">
              {gallery.map((item, index) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setActiveMedia(index)}
                  aria-label={`View ${index + 1} of ${gallery.length}`}
                  aria-current={index === activeMedia}
                  className={cn(
                    'relative size-20 shrink-0 overflow-hidden rounded-xl border-2 transition-all',
                    index === activeMedia
                      ? 'border-farm-600 ring-2 ring-farm-600/20'
                      : 'border-soil-200 opacity-60 hover:opacity-100',
                  )}
                >
                  <img
                    src={productImageUrl(item.storage_path) ?? ''}
                    alt=""
                    className="size-full object-cover"
                  />
                  {item.media_type === 'video' && (
                    <span className="absolute inset-0 flex items-center justify-center bg-soil-950/40">
                      <Video className="size-4 text-white" aria-hidden />
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ── Detail ── */}
        <div>
          {product.village && (
            <Link
              to="/farms"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-farm-700 hover:underline"
            >
              <MapPin className="size-4" aria-hidden />
              {product.village}
              {product.district && `, ${product.district}`}
            </Link>
          )}

          <h1 className="mt-2 font-display text-3xl font-black text-soil-900 sm:text-4xl">
            {product.title}
          </h1>
          {product.title_te && <p className="mt-1 text-lg text-soil-500">{product.title_te}</p>}

          <div className="mt-4 flex flex-wrap gap-2">
            {product.is_naturally_ripened && (
              <Badge variant="field">
                <Sun aria-hidden /> Naturally ripened
              </Badge>
            )}
            {product.is_chemical_free && (
              <Badge variant="farm">
                <Leaf aria-hidden /> No chemicals
              </Badge>
            )}
            {product.farm_verification === 'verified' && (
              <Badge variant="success">
                <ShieldCheck aria-hidden /> Farm verified
              </Badge>
            )}
            <Badge variant="neutral">{SOURCE_LABELS[product.source]}</Badge>
          </div>

          {/* ── Price ── */}
          <div className="mt-6 flex items-baseline gap-3">
            <span className="font-display text-4xl font-black text-soil-900">
              {formatRupeesCompact(product.price_paise)}
            </span>
            {product.compare_at_paise && (
              <span className="text-lg text-soil-400 line-through">
                {formatRupeesCompact(product.compare_at_paise)}
              </span>
            )}
            <span className="text-soil-500">
              per {product.unit_quantity !== 1 && `${product.unit_quantity} `}
              {UNIT_LABELS[product.unit]}
            </span>
          </div>

          {/* ── Freshness ── */}
          {(product.harvested_on || shelfLife !== null) && (
            <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 rounded-xl bg-farm-50 p-4">
              {product.harvested_on && (
                <div className="flex items-center gap-2 text-sm">
                  <CalendarDays className="size-4 text-farm-600" aria-hidden />
                  <span className="text-soil-600">Harvested</span>
                  <span className="font-semibold text-soil-900">
                    {relativeDay(product.harvested_on)}
                  </span>
                </div>
              )}
              {shelfLife !== null && shelfLife >= 0 && (
                <div className="flex items-center gap-2 text-sm">
                  <Leaf className="size-4 text-farm-600" aria-hidden />
                  <span className="text-soil-600">Best within</span>
                  <span className="font-semibold text-soil-900">
                    {shelfLife} {shelfLife === 1 ? 'day' : 'days'}
                  </span>
                </div>
              )}
            </div>
          )}

          {product.description && (
            <p className="mt-6 leading-relaxed text-soil-700">{product.description}</p>
          )}
          {product.harvest_note && (
            <p className="mt-3 border-l-2 border-harvest-400 pl-4 text-sm italic leading-relaxed text-soil-600">
              {product.harvest_note}
            </p>
          )}

          {/* ── Quantity + add ── */}
          <div className="mt-8 rounded-card border border-soil-200 bg-white p-5">
            {isOut ? (
              <div className="text-center">
                <p className="font-display text-lg font-bold text-soil-800">Sold out for today</p>
                <p className="mt-1 text-sm text-soil-500">
                  This is seasonal produce from one farm — it runs out. Check back tomorrow.
                </p>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between gap-4">
                  <span className="text-sm font-medium text-soil-700">Quantity</span>

                  <div className="flex items-center gap-1 rounded-xl border border-soil-200 p-1">
                    <button
                      type="button"
                      onClick={() => setQuantity(Math.max(product.min_order_qty, qty - 1))}
                      disabled={qty <= product.min_order_qty}
                      aria-label="Decrease quantity"
                      className="flex size-9 items-center justify-center rounded-lg text-soil-700 transition-colors hover:bg-soil-100 disabled:opacity-30"
                    >
                      <Minus className="size-4" aria-hidden />
                    </button>

                    <span className="w-16 text-center font-display text-lg font-bold tabular-nums text-soil-900">
                      {qty}
                      <span className="ml-0.5 text-xs font-normal text-soil-500">
                        {UNIT_LABELS[product.unit]}
                      </span>
                    </span>

                    <button
                      type="button"
                      onClick={() => setQuantity(Math.min(maxQty, qty + 1))}
                      disabled={qty >= maxQty}
                      aria-label="Increase quantity"
                      className="flex size-9 items-center justify-center rounded-lg text-soil-700 transition-colors hover:bg-soil-100 disabled:opacity-30"
                    >
                      <Plus className="size-4" aria-hidden />
                    </button>
                  </div>
                </div>

                <div className="mt-4 flex items-center justify-between border-t border-soil-100 pt-4">
                  <span className="text-sm text-soil-600">Total</span>
                  <span className="font-display text-2xl font-bold text-soil-900">
                    {formatRupees(lineTotal(product.price_paise, qty))}
                  </span>
                </div>

                <Button size="lg" full className="mt-4" onClick={handleAdd}>
                  <ShoppingBasket aria-hidden />
                  Add to basket
                </Button>

                {product.stock_qty <= 5 && (
                  <p className="mt-3 text-center text-xs font-medium text-sun-600">
                    Only {product.stock_qty} {UNIT_LABELS[product.unit]} left from this farm
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Farm story ── */}
      {(product.farm_story || product.farm_name) && (
        <Reveal className="mt-16">
          <div className="overflow-hidden rounded-card border border-soil-200 bg-gradient-to-br from-field-100 to-white">
            <div className="p-8 sm:p-10">
              <Badge variant="farm" size="md">
                <Sprout aria-hidden /> The farm
              </Badge>

              <h2 className="mt-4 font-display text-2xl font-bold text-soil-900">
                {product.farm_name}
              </h2>

              <p className="mt-1 flex items-center gap-1.5 text-sm text-soil-600">
                <MapPin className="size-3.5" aria-hidden />
                {[product.village, product.mandal, product.district, product.state]
                  .filter(Boolean)
                  .join(', ')}
              </p>

              {product.farmer_display_name && (
                <p className="mt-3 text-sm text-soil-600">
                  Grown by{' '}
                  <span className="font-semibold text-soil-900">{product.farmer_display_name}</span>
                </p>
              )}

              {product.farm_story && (
                <p className="mt-5 max-w-2xl leading-relaxed text-soil-700">{product.farm_story}</p>
              )}

              {product.farm_practices && product.farm_practices.length > 0 && (
                <div className="mt-6 flex flex-wrap gap-2">
                  {product.farm_practices.map((practice) => (
                    <Badge key={practice} variant="farm">
                      {/* Unknown tags render raw rather than vanishing — a
                          farmer's new practice tag shouldn't silently
                          disappear because we haven't labelled it yet. */}
                      {PRACTICE_LABELS[practice] ?? practice.replace(/_/g, ' ')}
                    </Badge>
                  ))}
                </div>
              )}

              {/* Deliberately absent: contact details. Customers get to know
                  where their food came from and who grew it — not the
                  farmer's phone number. Enforced by the public_products view,
                  which has no such column. */}
            </div>
          </div>
        </Reveal>
      )}
    </div>
  );
}

function ProductSkeleton() {
  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="grid gap-10 lg:grid-cols-2">
        <Skeleton className="aspect-square rounded-card" />
        <div className="space-y-4">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-10 w-3/4" />
          <Skeleton className="h-6 w-24" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-40 w-full rounded-card" />
        </div>
      </div>
    </div>
  );
}
